<template lang="pug">
.FcwAddress
  .FcwAddress__address
    span.FcwAddress__title {{ t('common__address') }}
    p(
      v-for="line in address"
      :key="line"
    ) {{ line }}
    .FcwAddress__accessibility
      .FcwAddress__accessibility__item(v-if="formattedDistance")
        FIcon(name="locationArrow")
        span {{ formattedDistance }}
      .FcwAddress__accessibility__item(v-if="formattedDuration")
        .FcwAdress__walkIcon
          WalkIcon
        span {{ formattedDuration }}

  .FcwAddress__route(v-if="showRoute")
    FButton.FcwAddress__openRoute(
      background="neutral--light-5"
      icon
      @click="openRoute"
    )
      FIcon(name="path")
    span {{ t('common__route') }}
</template>

<style lang="stylus">
.FcwAddress
  display flex
  align-items center
  justify-content space-between
  background var(--color--neutral--light-4)
  color var(--color--neutral--dark-3)
  gap rem(24)
  border-radius rem(8)
  padding rem(8) rem(16)

.FcwAddress__address
  p
    use-font('body-1')
    line-height rem(24)
    font-weight 400

.FcwAddress__title
  use-font('subtitle-1')

.FcwAddress__accessibility
  display flex
  gap rem(16)

.FcwAddress__accessibility__item
  display flex
  gap rem(4)
  align-items center

.FcwAddress__route
  display flex
  flex-direction column
  align-items center
  gap rem(4)

.FcwAddress__openRoute
  elevation-light(2)

.FcwAdress__walkIcon
  fill var(--color--neutral--dark-3)
  width rem(24)
  height rem(24)
</style>

<script setup lang="ts">
import { isNullish } from '@fifteen/shared-lib';

import WalkIcon from '@/assets/icons/walk.svg?component';

export interface FcwAddressProps {
  /**
   * Address to display, line by line
   */
  address: string[];
  /**
   * Show route CTA
   */
  showRoute?: boolean;
  /**
   * Coordinates of the address
   */
  coordinates?: CoordinatesObject;
  /**
   * Distance to the address, in meters
   */
  distance?: number | null;
  /**
   * Walking time to the address, in seconds
   */
  walkingTime?: number | null;
}

const props = withDefaults(defineProps<FcwAddressProps>(), {
  showRoute: false,
  coordinates: undefined,
  distance: undefined,
  walkingTime: undefined,
});
const { t } = useClientI18n();
const { formatDistance, formatDuration, loadPolyfills } = useLocaleFormat();

await loadPolyfills();

const formattedDistance = computed<string>(() => {
  if (
    isNullish(props.distance) ||
    (!isNullish(props.distance) && props.distance < 0)
  ) {
    return '';
  }

  return props.distance < 1000
    ? formatDistance(props.distance, {
        inputUnit: 'm',
        outputUnit: 'm',
        maximumFractionDigits: 0,
      })
    : formatDistance(props.distance, {
        inputUnit: 'm',
        outputUnit: 'km',
        maximumFractionDigits: 1,
      });
});

const formattedDuration = computed(() => {
  if (isNullish(props.walkingTime)) return '';

  if (props.walkingTime < 60) {
    return `< ${formatDuration(60, {
      format: ['minutes'],
      style: 'short',
    })}`;
  }

  const dayInSeconds = 24 * 60 * 60;

  return props.walkingTime > dayInSeconds
    ? formatDuration(props.walkingTime, {
        format: ['days', 'hours'],
        style: 'short',
      })
    : formatDuration(props.walkingTime, {
        format: ['hours', 'minutes'],
        style: 'short',
      });
});

async function openRoute(): Promise<void> {
  if (!props.coordinates) return;

  // TODO: rework for mobile
  await navigateTo(
    `https://www.google.com/maps/search/?api=1&query=${props.coordinates.lat},${props.coordinates.lng}`,
    {
      external: true,
      open: {
        target: '_blank',
      },
    }
  );
}
</script>
